
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CompanyPeriodSelector from '../../../shared/components/CompanyPeriodSelector';
import PageHeader from '../../../shared/components/PageHeader';
import { fetchSectorRatioAnalysis } from '../../../services/analysisSectorRatio';
import { listSectorRatios } from '../../../services/sectorRatios';
import { useLanguage } from '../../../app/i18n/LanguageContext';
import { exportSectorRatioPdf } from './sectorRatioPdfExport';
import { getAuthRole } from '../../../shared/auth/access';
import './SectorRatioAnalysisPage.css';

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const UI_TEXT = {
  'tr-TR': {
    title: 'Sektorel Benchmark Intelligence',
    description: 'CEO ve CFO icin sektor konumu, risk ve sapma gorunumu.',
    calculating: 'Hesaplaniyor...',
    exportingPdf: 'PDF olusturuluyor...',
    exportPdf: 'PDF Al',
    calculate: 'Analiz Et',
    reportingBasis: 'Raporlama Bazi',
    ifrsDefault: 'IFRS (Varsayilan)',
    vukOption: 'VUK',
    activeBasis: 'Analiz Bazi',
    basisMismatchNotice: 'Sirket oranlari IFRS bazinda, sektor benchmark VUK/EVDS bazinda hesaplanmaktadir.',
    benchmarkPeriod: 'Sektor Rasyo Donemi',
    selectPeriod: 'Donem seciniz',
    benchmarkSector: 'Benchmark Sektor',
    selectSector: 'Sektor seciniz',
    sectorBenchmarkLoadError: 'Sektor benchmark listesi yuklenemedi.',
    selectCompanyPeriod: 'Lutfen sirket ve donem secin.',
    selectBenchmarkPeriod: 'Lutfen sektor rasyo donemi secin.',
    selectBenchmarkSector: 'Lutfen benchmark olan bir sektor secin.',
    sectorRatioCalcError: 'Sektorel rasyo analizi hesaplanamadi.',
    strong: 'Guclu',
    balanced: 'Dengeli',
    risky: 'Riskli',
    noData: 'Veri Yok',
    lowRisk: 'Dusuk Risk',
    caution: 'Dikkat',
    alarm: 'Alarm',
    benchmarkScore: 'Benchmark Skoru',
    sectorAboveDensity: 'Sektor Ustu Yogunluk',
    leverageRisk: 'Kaldirac Riski',
    company: 'Sirket',
    sector: 'Sektor',
    aiSnapshot: 'AI Snapshot',
    score: 'Skor',
    verdict: 'Verdict',
    criticalDeviation: 'Kritik Sapma',
    criticalDeviations: 'Kritik Sapmalar',
    top5Deviation: 'Top 5 sapma',
    noDeviationData: 'Sapma verisi yok.',
    companySectorCompare: 'Sirket / Sektor Benchmark Karsilastirmasi',
    sortedByDeviation: 'Sapma buyuklugune gore sirali',
    noCompareData: 'Karsilastirma verisi bulunmuyor.',
    diff: 'Fark',
    comparativeBars: 'Comparative 3-Lu Bar Gorunumu',
    sectorSlice: 'Sektor dilimi',
    noDataFound: 'Veri yok.',
    executiveTable: 'Yonetici Tablosu',
    ratioCount: 'rasyo',
    ratio: 'Rasyo',
    deviation: 'Sapma',
    status: 'Durum',
    code: 'Kod',
    noAnalysisData: 'Gosterilecek analiz verisi yok.',
    dataQualityIndicator: 'Data Quality Indicator',
    trustScore: 'Guven Skoru',
    missingBenchmark: 'Eksik Benchmark',
    missingData: 'Eksik Veri',
    suspiciousData: 'Supheli Veri'
  },
  'en-US': {
    title: 'Sector Benchmark Intelligence',
    description: 'Sector position, risk and deviation view for CEOs and CFOs.',
    calculating: 'Calculating...',
    exportingPdf: 'Building PDF...',
    exportPdf: 'Export PDF',
    calculate: 'Analyze',
    reportingBasis: 'Reporting Basis',
    ifrsDefault: 'IFRS (Default)',
    vukOption: 'VUK',
    activeBasis: 'Analysis Basis',
    basisMismatchNotice: 'Company ratios are based on IFRS while sector benchmark stays on VUK/EVDS.',
    benchmarkPeriod: 'Sector Ratio Period',
    selectPeriod: 'Select period',
    benchmarkSector: 'Benchmark Sector',
    selectSector: 'Select sector',
    sectorBenchmarkLoadError: 'Sector benchmark list could not be loaded.',
    selectCompanyPeriod: 'Please select company and period.',
    selectBenchmarkPeriod: 'Please select sector ratio period.',
    selectBenchmarkSector: 'Please select a benchmark sector.',
    sectorRatioCalcError: 'Sector ratio analysis could not be calculated.',
    strong: 'Strong',
    balanced: 'Balanced',
    risky: 'Risky',
    noData: 'No Data',
    lowRisk: 'Low Risk',
    caution: 'Caution',
    alarm: 'Alarm',
    benchmarkScore: 'Benchmark Score',
    sectorAboveDensity: 'Above-Sector Density',
    leverageRisk: 'Leverage Risk',
    company: 'Company',
    sector: 'Sector',
    aiSnapshot: 'AI Snapshot',
    score: 'Score',
    verdict: 'Verdict',
    criticalDeviation: 'Critical Deviation',
    criticalDeviations: 'Critical Deviations',
    top5Deviation: 'Top 5 deviations',
    noDeviationData: 'No deviation data.',
    companySectorCompare: 'Company / Sector Benchmark Comparison',
    sortedByDeviation: 'Sorted by deviation magnitude',
    noCompareData: 'No comparison data.',
    diff: 'Diff',
    comparativeBars: 'Comparative Triple-Bar View',
    sectorSlice: 'Sector slice',
    noDataFound: 'No data.',
    executiveTable: 'Executive Table',
    ratioCount: 'ratios',
    ratio: 'Ratio',
    deviation: 'Deviation',
    status: 'Status',
    code: 'Code',
    noAnalysisData: 'No analysis data to display.',
    dataQualityIndicator: 'Data Quality Indicator',
    trustScore: 'Trust Score',
    missingBenchmark: 'Missing Benchmark',
    missingData: 'Missing Data',
    suspiciousData: 'Suspicious Data'
  }
};

const RATIO_TITLE_TRANSLATIONS = {
  debt_to_equity_ratio: 'Debt to Equity Ratio',
  return_on_equity: 'Return on Equity (ROE)',
  return_on_assets: 'Return on Assets (ROA)',
  net_profit_margin: 'Net Profit Margin'
};

const humanizeRatioKey = (key) => (key || '')
  .split('_')
  .filter(Boolean)
  .map((token) => token.charAt(0).toUpperCase() + token.slice(1))
  .join(' ');

const formatValue = (value, unit, locale = 'tr-TR') => {
  if (value === null || value === undefined) {
    return '-';
  }
  const number = Number(value);
  if (Number.isNaN(number)) {
    return '-';
  }
  if (unit === '%') {
    return `${(number * 100).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
  }
  return number.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 4 });
};

const formatSignedPercent = (value, locale = 'tr-TR') => {
  const number = Number(value);
  if (Number.isNaN(number)) {
    return '-';
  }
  return `${number > 0 ? '+' : ''}${number.toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
};

const formatPercent = (value, locale = 'tr-TR', digits = 1) => {
  const number = Number(value);
  if (Number.isNaN(number)) {
    return '-';
  }
  return `${number.toLocaleString(locale, { minimumFractionDigits: digits, maximumFractionDigits: digits })}%`;
};

const deviationPercent = (company, benchmark) => {
  const c = Number(company);
  const b = Number(benchmark);
  if (Number.isNaN(c) || Number.isNaN(b) || Math.abs(b) < 0.000001) {
    return null;
  }
  return ((c - b) / Math.abs(b)) * 100;
};

const scoreLabel = (score) => {
  if (score >= 75) {
    return 'Guclu';
  }
  if (score >= 50) {
    return 'Dengeli';
  }
  return 'Riskli';
};

const extractYear = (value) => {
  const matched = String(value || '').match(/\d{4}/);
  return matched ? Number(matched[0]) : null;
};

function SectorRatioAnalysisPage() {
  const { language } = useLanguage();
  const text = UI_TEXT[language] || UI_TEXT['tr-TR'];
  const t = useCallback((key) => text[key] || UI_TEXT['tr-TR'][key] || key, [text]);
  const isTenantUser = getAuthRole() === 'TENANT_USER';
  const translateRatioTitle = (item) => {
    if (!item) {
      return '';
    }
    if (!language.startsWith('en')) {
      return item.title;
    }
    return RATIO_TITLE_TRANSLATIONS[item.key] || humanizeRatioKey(item.key) || item.title;
  };
  const [selection, setSelection] = useState({ company: null, period: null, companyId: '', periodId: '' });
  const [benchmarkPeriods, setBenchmarkPeriods] = useState([]);
  const [sectorsByPeriodId, setSectorsByPeriodId] = useState({});
  const [selectedBenchmarkPeriodId, setSelectedBenchmarkPeriodId] = useState('');
  const [sectors, setSectors] = useState([]);
  const [selectedSectorId, setSelectedSectorId] = useState('');
  const [data, setData] = useState(null);
  const [basis, setBasis] = useState('IFRS');
  const [loading, setLoading] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [message, setMessage] = useState('');
  const pageCaptureRef = useRef(null);

  const hasSelection = Boolean(selection.companyId && selection.periodId);
  const rows = useMemo(() => data?.items || [], [data]);

  useEffect(() => {
    const loadBenchmarkSectors = async () => {
      try {
        const payload = await listSectorRatios({
          isActive: true,
          includeGlobal: true,
          offset: 0,
          limit: 5000
        });
        const periodBuckets = new Map();
        (payload?.items || []).forEach((item) => {
          if (!item?.periodId || !item?.sectorId || !item?.sectorCode || !item?.sectorName) {
            return;
          }
          if (!periodBuckets.has(item.periodId)) {
            periodBuckets.set(item.periodId, {
              id: item.periodId,
              code: item.periodCode,
              description: item.periodDescription,
              sectors: new Map()
            });
          }
          const bucket = periodBuckets.get(item.periodId);
          if (!bucket.sectors.has(item.sectorId)) {
            bucket.sectors.set(item.sectorId, {
              id: item.sectorId,
              code: item.sectorCode,
              name: item.sectorName
            });
          }
        });
        const periodList = Array.from(periodBuckets.values())
          .map((bucket) => ({ id: bucket.id, code: bucket.code, description: bucket.description }))
          .sort((left, right) => String(right.code || '').localeCompare(String(left.code || ''), 'tr-TR'));
        const sectorMap = {};
        periodBuckets.forEach((bucket, periodId) => {
          sectorMap[String(periodId)] = Array.from(bucket.sectors.values()).sort((left, right) =>
            String(left.code).localeCompare(String(right.code), 'tr-TR')
          );
        });
        setBenchmarkPeriods(periodList);
        setSectorsByPeriodId(sectorMap);
      } catch (error) {
        setBenchmarkPeriods([]);
        setSectorsByPeriodId({});
        setSectors([]);
        setMessage(error instanceof Error ? error.message : text.sectorBenchmarkLoadError);
      }
    };

    loadBenchmarkSectors();
  }, [language, text.sectorBenchmarkLoadError]);

  useEffect(() => {
    if (!benchmarkPeriods.length) {
      setSelectedBenchmarkPeriodId('');
      return;
    }
    const currentExists = benchmarkPeriods.some((period) => String(period.id) === String(selectedBenchmarkPeriodId));
    if (currentExists) {
      return;
    }
    const companyPeriodId = String(selection.periodId || '');
    const companyMatched = benchmarkPeriods.find((period) => String(period.id) === companyPeriodId);
    if (companyMatched) {
      setSelectedBenchmarkPeriodId(String(companyMatched.id));
      return;
    }
    const companyYear = extractYear(selection.period?.code);
    if (companyYear) {
      const candidate = benchmarkPeriods.find((period) => {
        const periodYear = extractYear(period.code);
        return periodYear && periodYear <= companyYear;
      });
      if (candidate) {
        setSelectedBenchmarkPeriodId(String(candidate.id));
        return;
      }
    }
    setSelectedBenchmarkPeriodId(String(benchmarkPeriods[0].id));
  }, [benchmarkPeriods, selectedBenchmarkPeriodId, selection.periodId, selection.period]);

  useEffect(() => {
    const currentSectors = sectorsByPeriodId[String(selectedBenchmarkPeriodId)] || [];
    setSectors(currentSectors);
  }, [sectorsByPeriodId, selectedBenchmarkPeriodId]);

  useEffect(() => {
    if (sectors.length === 0) {
      setSelectedSectorId('');
      return;
    }
    const companySectorRaw = String(
      selection.company?.sectorCode
      || selection.company?.sector
      || selection.company?.sectorName
      || ''
    )
      .trim()
      .toUpperCase();
    const matched = sectors.find((sector) => String(sector.code).trim().toUpperCase() === companySectorRaw)
      || sectors.find((sector) => String(sector.name).trim().toUpperCase() === companySectorRaw)
      || sectors[0];
    if (matched) {
      setSelectedSectorId(String(matched.id));
    }
  }, [selection.company, sectors]);

  const handleCalculate = async () => {
    if (!hasSelection) {
      setMessage(t('selectCompanyPeriod'));
      return;
    }
    if (!isTenantUser && !selectedBenchmarkPeriodId) {
      setMessage(t('selectBenchmarkPeriod'));
      return;
    }
    if (!isTenantUser && !selectedSectorId) {
      setMessage(t('selectBenchmarkSector'));
      return;
    }
    setLoading(true);
    setMessage('');
    try {
      const response = await fetchSectorRatioAnalysis(
        selection.companyId,
        selection.periodId,
        selectedSectorId || undefined,
        selectedBenchmarkPeriodId || undefined,
        basis
      );
      setData(response);
    } catch (error) {
      setData(null);
      setMessage(error instanceof Error ? error.message : t('sectorRatioCalcError'));
    } finally {
      setLoading(false);
    }
  };

  const summary = useMemo(() => ({
    aboveCount: data?.aboveCount || 0,
    belowCount: data?.belowCount || 0,
    equalCount: data?.equalCount || 0,
    noBenchmarkCount: data?.noBenchmarkCount || 0
  }), [data]);

  const benchmarkScore = useMemo(() => {
    const score = (summary.aboveCount * 8) - (summary.belowCount * 10) - (summary.noBenchmarkCount * 6) + 50;
    return clamp(score, 0, 100);
  }, [summary]);

  const benchmarkVerdictKey = scoreLabel(benchmarkScore);
  const benchmarkVerdict = benchmarkVerdictKey === 'Guclu' ? t('strong') : benchmarkVerdictKey === 'Dengeli' ? t('balanced') : t('risky');

  const leverageRow = rows.find((row) => row.key === 'debt_to_equity_ratio');
  const leverageValue = Number(leverageRow?.calculatedValue);
  const leverageSector = Number(leverageRow?.sectorValue || 1);
  const leverageRiskLevel = Number.isNaN(leverageValue)
    ? t('noData')
    : leverageValue <= leverageSector
      ? t('lowRisk')
      : leverageValue <= leverageSector * 1.35
        ? t('caution')
        : t('alarm');

  const preparedRows = useMemo(() => {
    return rows.map((row) => {
      const company = Number(row.calculatedValue || 0);
      const sector = row.sectorValue != null ? Number(row.sectorValue) : null;
      const deviation = row.deviationMessage ? null : (deviationPercent(company, sector ?? 0) ?? 0);
      const domain = Math.max(company, sector ?? 0, 0.0001) * 1.2;
      const comparison = String(row.comparison || '').toLowerCase();
      const issueType = comparison === 'no_benchmark' ? 'data_quality' : comparison === 'below' ? 'risk' : comparison === 'equal' ? 'neutral' : 'positive';
      return {
        ...row,
        company,
        sector,
        domain,
        deviation,
        deviationMessage: row.deviationMessage,
        issueType,
        cappedDeviation: deviation != null ? clamp(deviation, -250, 250) : 0
      };
    });
  }, [rows]);

  const absDev = (r) => (r.deviationMessage ? 0 : Math.abs(r.deviation ?? 0));
  const criticalRows = useMemo(
    () => [...preparedRows].sort((left, right) => absDev(right) - absDev(left)).slice(0, 5),
    [preparedRows]
  );

  const sortedRows = useMemo(
    () => [...preparedRows].sort((left, right) => absDev(right) - absDev(left)),
    [preparedRows]
  );

  const percentile = useMemo(() => {
    if (!preparedRows.length) {
      return 0;
    }
    const better = preparedRows.filter((row) => row.issueType === 'positive').length;
    return clamp((better / preparedRows.length) * 100, 0, 100);
  }, [preparedRows]);

  const missingCount = preparedRows.filter((row) => row.issueType === 'data_quality').length;
  const suspiciousCount = preparedRows.filter((row) => Math.abs(row.deviation) > 300).length;
  const trustScore = clamp(100 - (missingCount * 8) - (suspiciousCount * 5), 0, 100);

  const aiSnapshot = {
    score: benchmarkScore,
    verdict: benchmarkVerdict,
    critical: translateRatioTitle(criticalRows[0]) || '-'
  };

  const handleExportPdf = useCallback(async () => {
    if (!pageCaptureRef.current || exportingPdf) {
      return;
    }

    try {
      setExportingPdf(true);
      await exportSectorRatioPdf({
        element: pageCaptureRef.current,
        companyName: selection.company?.name || selection.company?.title || '',
        periodLabel: selection.period?.label || selection.period?.description || selection.period?.code || '',
        language
      });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : t('sectorRatioCalcError'));
    } finally {
      setExportingPdf(false);
    }
  }, [exportingPdf, language, selection.company, selection.period, t]);

  return (
    <div className="card sector-ratio-analysis-card" ref={pageCaptureRef}>
      <PageHeader
        title={t('title')}
        description={t('description')}
        actions={
          <div className="sector-ratio-page-actions" data-export-hidden="true">
            <button type="button" className="btn-secondary" disabled={!hasSelection || loading || exportingPdf} onClick={handleExportPdf}>
              {exportingPdf ? t('exportingPdf') : t('exportPdf')}
            </button>
            <button type="button" className="btn-primary" disabled={!hasSelection || loading || exportingPdf} onClick={handleCalculate}>
              {loading ? t('calculating') : t('calculate')}
            </button>
          </div>
        }
      />

      <CompanyPeriodSelector onSelectionChange={setSelection} locale={language} />

      <section className="sector-ratio-analysis-controls">
        <label htmlFor="sector-basis-select">
          {t('reportingBasis')}
          <select id="sector-basis-select" value={basis} onChange={(event) => setBasis(event.target.value)}>
            <option value="IFRS">{t('ifrsDefault')}</option>
            <option value="VUK">{t('vukOption')}</option>
          </select>
        </label>
        <label htmlFor="benchmark-period-select">
          {t('benchmarkPeriod')}
          <select
            id="benchmark-period-select"
            value={selectedBenchmarkPeriodId}
            onChange={(event) => setSelectedBenchmarkPeriodId(event.target.value)}
            disabled={isTenantUser || benchmarkPeriods.length === 0}
          >
            <option value="">{t('selectPeriod')}</option>
            {benchmarkPeriods.map((period) => (
              <option key={period.id} value={period.id}>
                {period.code || period.description || `${language.startsWith('en') ? 'Period' : 'Donem'} ${period.id}`}
              </option>
            ))}
          </select>
        </label>
        <label htmlFor="sector-select">
          {t('benchmarkSector')}
          <select
            id="sector-select"
            value={selectedSectorId}
            onChange={(event) => setSelectedSectorId(event.target.value)}
            disabled={isTenantUser || !selectedBenchmarkPeriodId || sectors.length === 0}
          >
            {isTenantUser && sectors.length === 0 ? (
              <option value="">
                {String(selection.company?.sectorCode || selection.company?.sector || selection.company?.sectorName || t('selectSector')).trim()}
              </option>
            ) : (
              <>
                <option value="">{t('selectSector')}</option>
                {sectors.map((sector) => (
                  <option key={sector.id} value={sector.id}>
                    {sector.code} - {sector.name}
                  </option>
                ))}
              </>
            )}
          </select>
        </label>
      </section>

      {message && <p className="sector-ratio-analysis-message">{message}</p>}
      {data?.reportingBasis && <p className="sector-ratio-analysis-message">{t('activeBasis')}: {data.reportingBasis}</p>}
      {basis === 'IFRS' && !message && <p className="sector-ratio-analysis-message">{t('basisMismatchNotice')}</p>}

      <section className="sr-executive-grid">
        <article className="sr-exec-card sr-gauge-card">
          <header><span>{t('benchmarkScore')}</span><strong>{formatValue(benchmarkScore, null, language)}</strong></header>
          <div className="sr-gauge" aria-hidden="true">
            <div className="sr-gauge-center">{formatValue(benchmarkScore, null, language)}</div>
          </div>
          <footer><span className={`sr-verdict sr-verdict-${benchmarkVerdict.toLowerCase()}`}>{benchmarkVerdict}</span></footer>
        </article>

        <article className="sr-exec-card">
          <header><span>{t('sectorAboveDensity')}</span><strong>{summary.aboveCount}/{preparedRows.length || 0}</strong></header>
          <div className="sr-progress"><span style={{ width: `${preparedRows.length ? (summary.aboveCount / preparedRows.length) * 100 : 0}%` }} /></div>
          <footer><span>{formatPercent(preparedRows.length ? (summary.aboveCount / preparedRows.length) * 100 : 0, language, 0)}</span></footer>
        </article>

        <article className="sr-exec-card">
          <header><span>{t('leverageRisk')}</span><strong>{leverageRiskLevel}</strong></header>
          <div className="sr-thermometer"><span style={{ height: `${Number.isNaN(leverageValue) || leverageSector <= 0 ? 0 : clamp((leverageValue / (leverageSector * 2)) * 100, 0, 100)}%` }} /></div>
          <footer><span>{t('company')}: {formatValue(leverageValue, null, language)} | {t('sector')}: {formatValue(leverageSector, null, language)}</span></footer>
        </article>

        <article className="sr-exec-card">
          <header><span>{t('aiSnapshot')}</span><strong>{aiSnapshot.verdict}</strong></header>
          <div className="sr-mini-snapshot">
            <div><small>{t('score')}</small><b>{formatValue(aiSnapshot.score, null, language)}</b></div>
            <div><small>{t('verdict')}</small><b>{aiSnapshot.verdict}</b></div>
            <div><small>{t('criticalDeviation')}</small><b>{aiSnapshot.critical}</b></div>
          </div>
        </article>
      </section>

      <section className="sr-section">
        <div className="sr-section-head"><h3>{t('criticalDeviations')}</h3><span>{t('top5Deviation')}</span></div>
        <div className="sr-diverging-list">
          {!criticalRows.length ? <p className="sr-empty">{t('noDeviationData')}</p> : criticalRows.map((row) => (
            <article key={row.key} className="sr-diverging-row">
              <div className="sr-diverging-head"><strong>{translateRatioTitle(row)}</strong><span>{row.deviationMessage || formatSignedPercent(row.deviation, language)}</span></div>
              <div className="sr-diverging-track"><span className="sr-diverging-center" /><span className={`sr-diverging-bar ${row.cappedDeviation >= 0 ? 'positive' : 'negative'}`} style={{ width: `${Math.min(50, Math.abs(row.cappedDeviation) / 5)}%`, [row.cappedDeviation >= 0 ? 'left' : 'right']: '50%' }} /></div>
            </article>
          ))}
        </div>
      </section>

      <section className="sr-section">
        <div className="sr-section-head"><h3>{t('companySectorCompare')}</h3><span>{t('sortedByDeviation')}</span></div>
        <div className="sr-bullet-list">
          {!sortedRows.length ? <p className="sr-empty">{t('noCompareData')}</p> : sortedRows.map((row) => (
            <article key={row.key} className="sr-bullet-row">
              <div className="sr-bullet-head"><strong>{translateRatioTitle(row)}</strong><span>{t('company')} {formatValue(row.company, row.unit, language)} | {t('sector')} {formatValue(row.sector, row.unit, language)} | {t('diff')} {row.deviationMessage || formatSignedPercent(row.deviation, language)}</span></div>
              <div className="sr-bullet-track">
                <span className="sr-bullet-zones" />
                <span className="sr-bullet-company" style={{ width: `${clamp((row.company / row.domain) * 100, 0, 100)}%` }} />
                <span className="sr-bullet-sector" style={{ left: `${row.sector != null ? clamp((row.sector / row.domain) * 100, 0, 100) : 0}%` }} />
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="sr-section">
        <div className="sr-section-head"><h3>{t('comparativeBars')}</h3><span>{t('sectorSlice')}: {formatPercent(percentile, language, 0)}</span></div>
        <div className="sr-compare-list">
          {!sortedRows.length ? <p className="sr-empty">{t('noDataFound')}</p> : sortedRows.map((row) => (
            <article key={`cmp-${row.key}`} className="sr-compare-row">
              <div className="sr-compare-title"><strong>{translateRatioTitle(row)}</strong><span>{row.sectorRatioCode || '-'}</span></div>
              <div className="sr-tri-bars">
                <div><small>{t('company')}</small><span style={{ width: `${clamp((row.company / row.domain) * 100, 0, 100)}%` }} /></div>
                <div><small>{t('sector')}</small><span style={{ width: `${row.sector != null ? clamp((row.sector / row.domain) * 100, 0, 100) : 0}%` }} /></div>
                <div><small>{t('diff')}</small><span style={{ width: `${row.deviationMessage ? 0 : clamp(Math.abs(row.deviation ?? 0) / 3, 0, 100)}%` }} /></div>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="sr-section">
        <div className="sr-section-head"><h3>{t('executiveTable')}</h3><span>{sortedRows.length} {t('ratioCount')}</span></div>
        <div className="sector-ratio-analysis-table-wrapper">
          <table className="sector-ratio-analysis-table">
            <thead>
              <tr>
                <th>{t('ratio')}</th>
                <th>{t('company')}</th>
                <th>{t('sector')}</th>
                <th>{t('deviation')}</th>
                <th>{t('status')}</th>
                <th>{t('code')}</th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="sector-ratio-analysis-empty">{loading ? t('calculating') : t('noAnalysisData')}</td>
                </tr>
              ) : (
                sortedRows.map((row) => (
                  <tr key={`table-${row.key}`}>
                    <td>{translateRatioTitle(row)}</td>
                    <td>{formatValue(row.company, row.unit, language)}</td>
                    <td>{formatValue(row.sector, row.unit, language)}</td>
                    <td>{row.deviationMessage || formatSignedPercent(row.deviation, language)}</td>
                    <td>
                      <span className={`sector-ratio-analysis-status sector-ratio-analysis-status-${String(row.comparison || '').toLowerCase()}`}>
                        {row.comparison}
                      </span>
                    </td>
                    <td>{row.sectorRatioCode || '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="sr-section">
        <div className="sr-section-head"><h3>{t('dataQualityIndicator')}</h3><span>{t('trustScore')} {formatPercent(trustScore, language, 0)}</span></div>
        <div className="sr-data-grid">
          <article><span>{t('missingBenchmark')}</span><strong>{summary.noBenchmarkCount}</strong></article>
          <article><span>{t('missingData')}</span><strong>{missingCount}</strong></article>
          <article><span>{t('suspiciousData')}</span><strong>{suspiciousCount}</strong></article>
          <article><span>{t('trustScore')}</span><strong>{formatPercent(trustScore, language, 0)}</strong></article>
        </div>
      </section>
    </div>
  );
}

export default SectorRatioAnalysisPage;
